import styled from "@emotion/styled";
import React from "react";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  height?: string;
}

// Drag distance (px) past which releasing the sheet dismisses it instead of
// springing back.
const DISMISS_DISTANCE = 80;
// Downward flick speed (px/ms) that dismisses regardless of distance.
const DISMISS_VELOCITY = 0.5;

const Backdrop = styled.div<{ open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 900;
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? "auto" : "none")};
  transition: opacity 0.3s;

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    display: none;
  }
`;

const Drawer = styled.div<{ open: boolean; height: string }>`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${({ height }) => height};
  background: #ffffff;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 20px rgba(78, 78, 81, 0.15);
  transform: translateY(${({ open }) => (open ? "0" : "100%")});
  transition: transform 0.4s ease;
  z-index: 950;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    display: none;
  }
`;

// The grab strip: tap to close, drag down to dismiss. The 4px pill alone is far
// below a usable touch target, so the whole 44px-tall strip is the hit area.
// touch-action: none keeps the browser from claiming the vertical gesture as a
// page scroll mid-drag.
const HandleArea = styled.div`
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: none;
`;

const Handle = styled.div`
  width: 40px;
  height: 4px;
  background: rgba(137, 144, 147, 0.4);
  border-radius: 2px;
  pointer-events: none;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: env(safe-area-inset-right, 0px);
  padding-bottom: var(--tabbar-inset, env(safe-area-inset-bottom, 0px));
`;

const MobileDrawer: React.FC<MobileDrawerProps> = ({
  open,
  onClose,
  children,
  height = "60vh",
}) => {
  const [dragY, setDragY] = React.useState(0);
  const dragging = React.useRef(false);
  // Start of the current gesture, used for both distance and flick velocity.
  const origin = React.useRef<{ y: number; time: number }>({ y: 0, time: 0 });
  // A gesture that never moved is a tap, which closes the sheet.
  const moved = React.useRef(false);

  // A sheet re-opened after a drag must not inherit the old offset.
  React.useEffect(() => {
    if (open) setDragY(0);
  }, [open]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = true;
    moved.current = false;
    origin.current = { y: e.clientY, time: e.timeStamp };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const dy = e.clientY - origin.current.y;
    if (Math.abs(dy) > 3) moved.current = true;
    // Downward only — dragging up must not lift the sheet off its anchor.
    setDragY(dy > 0 ? dy : 0);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    dragging.current = false;

    const dy = e.clientY - origin.current.y;
    const elapsed = e.timeStamp - origin.current.time;
    const velocity = elapsed > 0 ? dy / elapsed : 0;

    if (!moved.current || dy > DISMISS_DISTANCE || velocity > DISMISS_VELOCITY) {
      // Let the sheet finish the slide from wherever the finger left it.
      setDragY(0);
      onClose();
      return;
    }

    setDragY(0);
  };

  return (
    <>
      <Backdrop open={open} onClick={onClose} />
      <Drawer
        open={open}
        height={height}
        style={
          dragY > 0
            ? { transform: `translateY(${dragY}px)`, transition: "none" }
            : undefined
        }
      >
        <HandleArea
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <Handle />
        </HandleArea>
        <Content>{children}</Content>
      </Drawer>
    </>
  );
};

export default MobileDrawer;
